import { useTranslation } from 'react-i18next';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import WhatsAppFloat from '../../home/components/WhatsAppFloat';
import SEO from '@/components/SEO';

export default function CommercePage() {
  const { t } = useTranslation('navigation');
  const isEn = t('home') === 'Home';

  const solutions = [
    {
      titleFr: 'Gestion des Stocks Intelligente', titleEn: 'Intelligent Stock Management',
      descFr: 'Système complet de suivi et contrôle des stocks avec alertes automatiques', descEn: 'Complete stock tracking and control system with automatic alerts',
      icone: 'ri-archive-line',
      featuresFr: ['Inventaire temps réel multi-entrepôts avec localisation précise', 'Alertes automatiques de stock minimum et réapprovisionnement', 'Traçabilité complète des mouvements de produits', "Gestion des lots, numéros de série et dates d'expiration", "Optimisation des commandes fournisseurs selon l'historique", 'Valorisation des stocks (FIFO, LIFO, coût moyen)', 'Inventaires physiques avec écarts automatiques'],
      featuresEn: ['Real-time multi-warehouse inventory with precise location', 'Automatic minimum stock and replenishment alerts', 'Complete product movement traceability', 'Batch, serial number and expiry date management', 'Supplier order optimization based on history', 'Stock valuation (FIFO, LIFO, average cost)', 'Physical inventories with automatic discrepancies'],
    },
    {
      titleFr: 'Point de Vente (POS) Avancé', titleEn: 'Advanced Point of Sale (POS)',
      descFr: 'Caisse moderne et rapide avec gestion complète des ventes', descEn: 'Modern and fast POS with complete sales management',
      icone: 'ri-shopping-cart-line',
      featuresFr: ['Interface tactile intuitive et rapide', 'Gestion des promotions, remises et coupons', 'Programme de fidélité client intégré', 'Paiements multiples (espèces, carte bancaire, mobile money)', 'Tickets de caisse et factures personnalisables', 'Mode hors ligne pour continuité de service', 'Gestion des retours et échanges', 'Scan de codes-barres et QR codes'],
      featuresEn: ['Intuitive and fast touchscreen interface', 'Promotions, discounts and coupons management', 'Integrated client loyalty program', 'Multiple payments (cash, card, mobile money)', 'Customizable receipts and invoices', 'Offline mode for service continuity', 'Returns and exchanges management', 'Barcode and QR code scanning'],
    },
    {
      titleFr: 'Gestion Fournisseurs', titleEn: 'Supplier Management',
      descFr: 'Optimisation complète des relations et achats fournisseurs', descEn: 'Complete optimization of supplier relations and purchases',
      icone: 'ri-truck-line',
      featuresFr: ["Base de données fournisseurs complète avec historique", "Comparaison automatique des prix et conditions", "Gestion des bons de commande et appels d'offres", 'Suivi des livraisons et réceptions', 'Évaluation de la performance fournisseurs', 'Gestion des contrats et conditions de paiement', 'Alertes de renouvellement de commandes'],
      featuresEn: ['Complete supplier database with history', 'Automatic price and condition comparison', 'Purchase orders and RFQ management', 'Delivery and reception tracking', 'Supplier performance evaluation', 'Contract and payment terms management', 'Order renewal alerts'],
    },
    {
      titleFr: 'E-commerce Intégré', titleEn: 'Integrated E-commerce',
      descFr: 'Boutique en ligne synchronisée avec votre magasin physique', descEn: 'Online store synchronized with your physical store',
      icone: 'ri-store-line',
      featuresFr: ['Catalogue produits synchronisé automatiquement', 'Gestion centralisée des commandes en ligne et magasin', 'Paiements sécurisés multi-devises', "Options de livraison et retrait en magasin", 'Marketing digital et promotions intégrés', 'Gestion des avis clients', 'Tableaux de bord e-commerce détaillés'],
      featuresEn: ['Automatically synchronized product catalog', 'Centralized online and in-store order management', 'Secure multi-currency payments', 'Delivery and in-store pickup options', 'Integrated digital marketing and promotions', 'Customer review management', 'Detailed e-commerce dashboards'],
    },
    {
      titleFr: 'Analyses et Reporting', titleEn: 'Analytics & Reporting',
      descFr: 'Tableaux de bord intelligents pour optimiser vos performances', descEn: 'Smart dashboards to optimize your performance',
      icone: 'ri-bar-chart-line',
      featuresFr: ["Analyse des ventes par période, produit, catégorie", 'Produits les plus vendus et tendances', 'Marges et rentabilité par produit', "Prévisions de ventes basées sur l'historique", 'Rapports personnalisables et automatisés', 'Analyse du comportement client', 'Indicateurs de performance (KPIs) en temps réel'],
      featuresEn: ['Sales analysis by period, product, category', 'Best sellers and trends', 'Margins and profitability by product', 'Sales forecasts based on history', 'Customizable and automated reports', 'Client behavior analysis', 'Real-time performance indicators (KPIs)'],
    },
    {
      titleFr: 'Gestion Multi-magasins', titleEn: 'Multi-Store Management',
      descFr: 'Centralisation et contrôle de plusieurs points de vente', descEn: 'Centralization and control of multiple points of sale',
      icone: 'ri-building-line',
      featuresFr: ['Vue consolidée de tous vos magasins', 'Transferts de stock inter-magasins simplifiés', 'Gestion centralisée des prix et promotions', 'Reporting consolidé et comparatif', 'Gestion des utilisateurs et droits par magasin', 'Synchronisation automatique des données', 'Contrôle centralisé des performances'],
      featuresEn: ['Consolidated view of all your stores', 'Simplified inter-store stock transfers', 'Centralized price and promotions management', 'Consolidated and comparative reporting', 'User and rights management by store', 'Automatic data synchronization', 'Centralized performance control'],
    },
  ];

  const advantages = [
    { icon: 'ri-line-chart-line', titleKey: 'commerceAdv1Title', descKey: 'commerceAdv1Desc' },
    { icon: 'ri-arrow-up-line', titleKey: 'commerceAdv2Title', descKey: 'commerceAdv2Desc' },
    { icon: 'ri-heart-line', titleKey: 'commerceAdv3Title', descKey: 'commerceAdv3Desc' },
    { icon: 'ri-time-line', titleKey: 'commerceAdv4Title', descKey: 'commerceAdv4Desc' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Logiciel Commerce & Distribution - Alpha Solutions | POS & Stock"
        description="Solution complète pour commerces et distribution : POS avancé, gestion des stocks intelligente, e-commerce intégré, fournisseurs et analyses de ventes."
        keywords="logiciel commerce Cameroun, POS distribution Douala, gestion stock magasin, système caisse commerce"
        canonicalPath="/secteurs/commerce"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Logiciel Commerce & Distribution Alpha Solutions",
          "description": "Solution POS, stock et e-commerce pour commerces au Cameroun",
          "brand": { "@type": "Organization", "name": "Alpha Solutions" }
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center py-20"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)), url('https://readdy.ai/api/search-image?query=modern%20african%20retail%20store%20with%20advanced%20inventory%20management%20system%20and%20digital%20point%20of%20sale%20technology%2C%20professional%20commercial%20space%20with%20organized%20product%20displays%20and%20efficient%20customer%20service%20operations&width=1200&height=600&seq=commerce-hero&orientation=landscape')` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('commerceHeroTitle')}</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{t('commerceHeroDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>{t('sectorDemoBtn')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('commerceFeaturesTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('commerceFeaturesSubtitle')}</p>
            <div className="mt-6 inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full">
              <i className="ri-settings-3-line mr-2"></i>{t('commerceCustomBadge')}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((sol, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className={`${sol.icone} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{isEn ? sol.titleEn : sol.titleFr}</h3>
                <p className="text-gray-600 mb-4">{isEn ? sol.descEn : sol.descFr}</p>
                <ul className="space-y-2">
                  {(isEn ? sol.featuresEn : sol.featuresFr).map((f, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-blue-50 rounded-lg p-8 border-l-4 border-blue-500">
            <h3 className="text-2xl font-bold text-blue-900 mb-4">{t('sectorHowWeHelp')}</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: 'ri-search-line', titleKey: 'sectorAnalyzeNeeds', descKey: 'sectorAnalyzeNeedsDesc' },
                { icon: 'ri-lightbulb-line', titleKey: 'sectorExpertReco', descKey: 'sectorExpertRecoDesc' },
                { icon: 'ri-tools-line', titleKey: 'sectorIntegration', descKey: 'sectorIntegrationDesc' },
                { icon: 'ri-user-star-line', titleKey: 'sectorFormationSupport', descKey: 'sectorFormationSupportDesc' },
              ].map((item, i) => (
                <div key={i}>
                  <h4 className="font-semibold text-blue-800 mb-2"><i className={`${item.icon} mr-2`}></i>{t(item.titleKey)}</h4>
                  <p className="text-blue-700">{t(item.descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Avantages Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('commerceWeBringTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('commerceWeBringSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((adv, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${adv.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t(adv.titleKey)}</h3>
                <p className="text-gray-600">{t(adv.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignage Client */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 rounded-lg p-8 border-l-4 border-blue-500">
            <div className="flex items-center mb-6">
              <img src="https://readdy.ai/api/search-image?query=professional%20african%20business%20owner%20Gaby%20Mbomgnin%20in%20modern%20distribution%20warehouse%2C%20confident%20commercial%20director%20managing%20successful%20distribution%20company%20with%20digital%20inventory%20systems&width=100&height=100&seq=gaby-mbomgnin-md&orientation=squarish"
                alt={t('commerceTestiName')} className="w-16 h-16 rounded-full object-cover mr-4" />
              <div>
                <h3 className="text-xl font-bold text-blue-900">{t('commerceTestiName')}</h3>
                <p className="text-blue-700">{t('commerceTestiRole')}</p>
              </div>
            </div>
            <blockquote className="text-lg text-blue-800 italic mb-4">"{t('commerceTestiQuote')}"</blockquote>
            <div className="flex items-center">
              <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill"></i>)}</div>
              <span className="ml-2 text-blue-700 font-medium">{t('commerceSatisfied')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('commerceCtaTitle')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">{t('commerceCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line mr-2"></i>{t('sectorFreeDemo')}
            </a>
            <a href="/solutions" className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
              {t('sectorViewSolutions')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
